import type { TemplateRef } from '@angular/core';
import {
    type AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    Input,
    type OnDestroy,
    ViewChild,
    ViewContainerRef,
    DOCUMENT,
} from '@angular/core';
import { WebBaseControlTypedSettingsComponent } from '@web/widgets/common/abstract/web-base-control-typed-settings.component';
import type { ProductCardWidgetData } from '@web/widgets/product-card/type';
import { type ModalSettingsFormGroup, type ModalSettingsFormType } from '@web/widgets/product-card/type';
import { previewPortalAttachAction } from '@web/store/page-structure/widgets/widgets.actions';
import type { Classes } from 'jss';
import { Store } from '@ngrx/store';

import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { TranslateModule } from '@ngx-translate/core';
import { ProductVariantsSettingsComponent } from '../common-settings/product-variants-settings/product-variants-settings.component';
import { TypographySettingsComponent } from '../common-settings/typography-settings/typography-settings.component';
import { GeneralSettingsComponent } from '../common-settings/general-settings/general-settings.component';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';
import { ProductCardModalComponent } from './product-card-modal/product-card-modal.component';

@Component({
    selector: 'web-modal-settings',
    templateUrl: './modal-settings.component.html',
    styleUrls: ['./modal-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ProductCardModalComponent,
        WebSettingsModule,
        GeneralSettingsComponent,
        TypographySettingsComponent,
        ProductVariantsSettingsComponent,
        TranslateModule,
    ],
})
export class ModalSettingsComponent
    extends WebBaseControlTypedSettingsComponent<ModalSettingsFormGroup>
    implements AfterViewInit, OnDestroy
{
    @Input() data: ProductCardWidgetData;
    @Input() classes: Classes;

    @ViewChild('modalPreviewPortal') public modalPreviewPortal: TemplateRef<unknown>;

    private store$: Store = inject(Store);
    private document: Document = inject(DOCUMENT);
    private viewContainerRef: ViewContainerRef = inject(ViewContainerRef);
    private portalHost: DomPortalOutlet;

    public ngAfterViewInit(): void {
        const portalHTMLElement = this.document.querySelector('#modalPreviewContent');
        this.portalHost = new DomPortalOutlet(portalHTMLElement);

        this.portalHost.attach(new TemplatePortal(this.modalPreviewPortal, this.viewContainerRef));
        this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: true }));
    }

    public get generalSettingsFormGroup(): ModalSettingsFormType['general'] {
        return this.itemForm.get('general') as ModalSettingsFormType['general'];
    }

    public get typographySettingsFormGroup(): ModalSettingsFormType['typography'] {
        return this.itemForm.get('typography') as ModalSettingsFormType['typography'];
    }

    public get productVariantsSettingsFormGroup(): ModalSettingsFormType['productVariants'] {
        return this.itemForm.get('productVariants') as ModalSettingsFormType['productVariants'];
    }

    public ngOnDestroy(): void {
        this.portalHost.detach();
        this.store$.dispatch(previewPortalAttachAction.modalPreviewToggle({ attached: false }));
    }
}
